import type { HeroBlockPayload } from '@opinix-cloud/shared';

interface HeroProps {
  onRequestDemo: () => void;
  content: HeroBlockPayload;
}

/**
 * Hero — fully data-driven. The block payload supplies headline,
 * accent (the subspan that gets the cyan highlight), subhead, two
 * CTAs, badge and footnote. Visual chrome (grid pattern, radial
 * accents, edge fades) stays hardcoded — the CMS never writes JSX.
 */
export function Hero({ onRequestDemo, content }: HeroProps): JSX.Element {
  const { headline, headlineAccent } = content;
  const headlineParts = splitHeadline(headline, headlineAccent ?? null);

  return (
    <section className="relative overflow-hidden bg-navy-900 pt-32 pb-24 sm:pt-40 sm:pb-32">
      {/* Faint grid pattern */}
      <div
        className="pointer-events-none absolute inset-0 bg-grid-faint bg-grid-32 opacity-60"
        aria-hidden="true"
      />
      {/* Cyan + ember radial accents */}
      <div
        className="pointer-events-none absolute inset-0 bg-radial-cyan"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-radial-ember"
        aria-hidden="true"
      />
      {/* Top edge fade */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-navy-950/60 to-transparent"
        aria-hidden="true"
      />

      <div className="container-x relative">
        <div className="max-w-3xl">
          {content.badge && (
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" />
              </span>
              {content.badge}
            </div>
          )}
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tightest text-white leading-[1.05]">
            {headlineParts.before}
            {headlineParts.accent && (
              <span className="text-cyan-300">{headlineParts.accent}</span>
            )}
            {headlineParts.after}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-steel-300 max-w-2xl leading-relaxed">
            {content.subhead}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onRequestDemo}
              className="btn btn-primary"
            >
              {content.primaryCtaLabel}
              <svg
                aria-hidden="true"
                className="ml-2 h-4 w-4"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M4 10h12m-4-4 4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {content.secondaryCtaLabel && (
              <a
                href={content.secondaryCtaHref ?? '#early-access'}
                className="btn btn-secondary"
              >
                {content.secondaryCtaLabel}
              </a>
            )}
          </div>
          {content.footnote && (
            <p className="mt-6 text-sm text-steel-400">
              {content.footnote}{' '}
              <a
                href="#deployment"
                className="text-cyan-400 hover:text-cyan-300 underline-offset-4 hover:underline"
              >
                Pick what fits your agency.
              </a>
            </p>
          )}
        </div>
      </div>

      {/* Bottom edge fade */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-steel-50 to-transparent"
        aria-hidden="true"
      />
    </section>
  );
}

/**
 * Split the headline around the accent substring. If `accent` is null
 * or not found in `headline`, returns the whole string in `before` so
 * the JSX still renders identically (just without the highlight span).
 */
function splitHeadline(
  headline: string,
  accent: string | null,
): { before: string; accent: string | null; after: string } {
  if (!accent) return { before: headline, accent: null, after: '' };
  const idx = headline.indexOf(accent);
  if (idx < 0) return { before: headline, accent: null, after: '' };
  return {
    before: headline.slice(0, idx),
    accent,
    after: headline.slice(idx + accent.length),
  };
}
